import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Dialog, EmptyState, Field, Input, Section, Select, Spinner } from '@zt/ui';
import { api } from '../lib/api';
import { HelpTip } from '../lib/help';
import { useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import { TrPlaceFields } from '../lib/tr-place';
import { ConfirmButton } from './Confirm';
import type { CompanyLocation } from '../lib/types';

/**
 * Firma lokasyonları (F7-04, ADR-107): bayinin şube, teslimat ve fatura adresleri. Bayi kullanıcıları vitrinde
 * ödeme adımında seçer; panelden siparişte adres alanlarını doldurmak için kullanılır.
 */

interface Draft {
  label: string;
  fullName: string;
  phone: string;
  line1: string;
  line2: string;
  district: string;
  city: string;
  postalCode: string;
  isDefaultShipping: boolean;
  isDefaultBilling: boolean;
}

const EMPTY: Draft = {
  label: '',
  fullName: '',
  phone: '',
  line1: '',
  line2: '',
  district: '',
  city: '',
  postalCode: '',
  isDefaultShipping: false,
  isDefaultBilling: false,
};

const draftOf = (l: CompanyLocation): Draft => ({
  label: l.label ?? '',
  fullName: l.fullName,
  phone: l.phone ?? '',
  line1: l.line1,
  line2: l.line2 ?? '',
  district: l.district ?? '',
  city: l.city,
  postalCode: l.postalCode ?? '',
  isDefaultShipping: l.isDefaultShipping,
  isDefaultBilling: l.isDefaultBilling,
});

export function useCompanyLocations(companyId: string | null | undefined) {
  return useQuery({
    queryKey: ['company-locations', companyId],
    queryFn: () => api.get<CompanyLocation[]>(`/v1/admin/companies/${companyId}/locations`),
    enabled: !!companyId,
  });
}

export const locationLine = (l: Pick<CompanyLocation, 'line1' | 'district' | 'city'>) =>
  [l.line1, l.district, l.city].filter(Boolean).join(', ');

export function CompanyLocationsSection({ companyId }: { companyId: string }) {
  const t = useT();
  const { can } = usePermissions();
  const manage = can('b2b.dealer.manage');
  const list = useCompanyLocations(companyId);
  const [editing, setEditing] = useState<CompanyLocation | 'new' | null>(null);
  const remove = useApiMutation(
    (locationId: string) => api.del(`/v1/admin/companies/${companyId}/locations/${locationId}`),
    { invalidate: [['company-locations', companyId]], success: t('admin.locations.deleted') },
  );
  return (
    <Section
      title={t('admin.locations.title')}
      description={t('admin.locations.lead')}
      action={
        <span className="inline-flex items-center gap-1">
          {manage && (
            <Button size="sm" onClick={() => setEditing('new')}>
              {t('admin.locations.add')}
            </Button>
          )}
          <HelpTip k="dealers.locations" />
        </span>
      }
    >
      {!list.data ? (
        <Spinner label={t('common.loading')} />
      ) : !list.data.length ? (
        <EmptyState title={t('admin.locations.empty')} description={t('admin.locations.emptyLead')} />
      ) : (
        <ul className="divide-y divide-line rounded-md border border-line">
          {list.data.map((l) => (
            <li key={l.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="font-medium">{l.label}</p>
                <p className="text-sm text-ink-3">
                  {[l.fullName, l.phone, locationLine(l)].filter(Boolean).join(' · ')}
                </p>
              </div>
              {l.isDefaultShipping && <Badge tone="pine">{t('admin.locations.defaultShipping')}</Badge>}
              {l.isDefaultBilling && <Badge tone="slate">{t('admin.locations.defaultBilling')}</Badge>}
              {manage && (
                <>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(l)}>
                    {t('common.edit')}
                  </Button>
                  <ConfirmButton
                    message={t('admin.locations.confirmDelete', { name: l.label ?? '' })}
                    onConfirm={() => remove.mutateAsync(l.id)}
                  >
                    {t('common.delete')}
                  </ConfirmButton>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      {editing && (
        <LocationDialog
          companyId={companyId}
          location={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </Section>
  );
}

function LocationDialog({
  companyId,
  location,
  onClose,
}: {
  companyId: string;
  location: CompanyLocation | null;
  onClose: () => void;
}) {
  const t = useT();
  const [d, setD] = useState<Draft>(location ? draftOf(location) : EMPTY);
  const set = (patch: Partial<Draft>) => setD((x) => ({ ...x, ...patch }));
  const body = {
    label: d.label.trim(),
    fullName: d.fullName.trim(),
    phone: d.phone.trim() || null,
    line1: d.line1.trim(),
    line2: d.line2.trim() || null,
    district: d.district || null,
    city: d.city,
    postalCode: d.postalCode.trim() || null,
    countryCode: 'TR',
    isDefaultShipping: d.isDefaultShipping,
    isDefaultBilling: d.isDefaultBilling,
  };
  const save = useApiMutation(
    () =>
      location
        ? api.patch(`/v1/admin/companies/${companyId}/locations/${location.id}`, body)
        : api.post(`/v1/admin/companies/${companyId}/locations`, body),
    {
      invalidate: [['company-locations', companyId]],
      success: t('admin.locations.saved'),
      silentFieldErrors: true,
      onSuccess: onClose,
    },
  );
  const err = (f: string) => fieldError(save.error, f);
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={location ? t('admin.locations.edit') : t('admin.locations.add')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!d.label.trim() || !d.fullName.trim() || !d.line1.trim() || !d.city}
            onClick={() => save.mutate(undefined)}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          label={t('admin.locations.label')}
          required
          help={<HelpTip k="dealers.location_label" />}
          error={err('label')}
          className="sm:col-span-2"
        >
          {(p) => (
            <Input {...p} maxLength={40} value={d.label} onChange={(e) => set({ label: e.target.value })} />
          )}
        </Field>
        <Field label={t('admin.locations.contact')} required error={err('fullName')}>
          {(p) => <Input {...p} value={d.fullName} onChange={(e) => set({ fullName: e.target.value })} />}
        </Field>
        <Field label={t('admin.customers.phone')} error={err('phone')}>
          {(p) => (
            <Input {...p} type="tel" value={d.phone} onChange={(e) => set({ phone: e.target.value })} />
          )}
        </Field>
        <Field label={t('admin.orders.addressLine')} required error={err('line1')} className="sm:col-span-2">
          {(p) => <Input {...p} value={d.line1} onChange={(e) => set({ line1: e.target.value })} />}
        </Field>
        <TrPlaceFields
          city={d.city}
          district={d.district}
          onChange={(v) => set(v)}
          cityLabel={t('admin.dealers.city')}
          districtLabel={t('admin.dealers.district')}
          required
          cityError={err('city')}
          districtError={err('district')}
        />
        <Field label={t('admin.locations.postalCode')} error={err('postalCode')}>
          {(p) => (
            <Input
              {...p}
              inputMode="numeric"
              value={d.postalCode}
              onChange={(e) => set({ postalCode: e.target.value })}
            />
          )}
        </Field>
      </div>
      <div className="mt-4 space-y-2 text-sm">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            className="accent-[var(--zt-pine)]"
            checked={d.isDefaultShipping}
            onChange={(e) => set({ isDefaultShipping: e.target.checked })}
          />
          {t('admin.locations.makeDefaultShipping')}
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            className="accent-[var(--zt-pine)]"
            checked={d.isDefaultBilling}
            onChange={(e) => set({ isDefaultBilling: e.target.checked })}
          />
          {t('admin.locations.makeDefaultBilling')}
        </label>
      </div>
    </Dialog>
  );
}

/** Panelden siparişte: müşterinin bayi firmasının lokasyonuyla adres alanlarını doldurur (F7-04). */
export function LocationFill({
  companyId,
  onPick,
}: {
  companyId: string;
  onPick: (l: CompanyLocation) => void;
}) {
  const t = useT();
  const list = useCompanyLocations(companyId);
  if (!list.data?.length) return null;
  return (
    <Field label={t('admin.locations.fill')} help={<HelpTip k="orders.location_fill" />} className="max-w-sm">
      {(p) => (
        <Select
          {...p}
          value=""
          onChange={(e) => {
            const l = list.data!.find((x) => x.id === e.target.value);
            if (l) onPick(l);
          }}
        >
          <option value="">{t('admin.locations.choose')}</option>
          {list.data!.map((l) => (
            <option key={l.id} value={l.id}>
              {l.label} — {locationLine(l)}
            </option>
          ))}
        </Select>
      )}
    </Field>
  );
}
